/**
 * MapPopup — the popup body that appears when an operator/customer clicks
 * a smartbox pin on the map. Extracted from MapPage so the page module
 * stays focused on map state + controls; the popup is its own concern.
 *
 * Customer-visible fields only render when `operatorView=false`; the
 * Stacked toggle + SignalDots + version chip are operator-only.
 */
import React from "react";
import { Link } from "react-router-dom";

import AntennaStrip from "./AntennaStrip";
import PeplinkClientIcons from "../peplink/PeplinkClientIcons";
import PlugOutletResetButton from "../plugs/PlugOutletResetButton";
import ReportMaintenanceButton from "../ReportMaintenanceButton";
import SignalDot from "../SignalDot";
import StackedButton from "../StackedButton";
import StateBadge from "../StateBadge";
import { fmtAgo, fmtCustomerWc, fmtDuration } from "../../lib/format";
import { deriveSignalDots } from "../../lib/signalDots";
import type { Box, NvmeHealth } from "../../types/boxes";

/**
 * One-line NVMe summary for the operator popup: nvme-cli install status and,
 * once installed, unclean vs total power cycles from the SMART baseline.
 * Red on any media error (the drive itself), amber when most power cycles
 * were unclean (site power) or nvme-cli is still missing.
 */
function nvmeLine(nv: NvmeHealth): { text: string; tone: string; title: string } {
  if (nv.status !== "installed") {
    return {
      text: "nvme-cli not installed",
      tone: "text-warning-700",
      title:
        nv.status === "unreachable"
          ? "nvme-cli install attempted but the box was unreachable"
          : "nvme-cli pending -- box was offline during the rollout",
    };
  }
  const unsafe = nv.unsafe_shutdowns;
  const cycles = nv.power_cycles;
  const pct = unsafe != null && cycles ? Math.round((100 * unsafe) / cycles) : null;
  const media = nv.media_errors ?? 0;
  const tone =
    media > 0
      ? "text-error-700"
      : pct != null && pct >= 50
        ? "text-warning-700"
        : "text-secondary-600";
  const text =
    unsafe != null && cycles != null
      ? `unsafe ${unsafe}/${cycles} cycles${pct != null ? ` (${pct}%)` : ""} · ${media} media err`
      : "nvme-cli installed";
  const title = [
    nv.model,
    nv.percentage_used != null ? `wear ${nv.percentage_used}%` : null,
    nv.installed_version,
    nv.smart_read_utc ? `SMART read ${nv.smart_read_utc} UTC` : null,
    "Unsafe ~= cycles with 0 media errors means site power, not the drive",
  ]
    .filter(Boolean)
    .join("\n");
  return { text, tone, title };
}

/**
 * Hover text for the Starlink router client count: the actual device names,
 * so an operator can see WHICH things are on the router without opening the
 * box. Mirrors how the Peplink client list surfaces detail on hover.
 *
 * The router's own self-entry (`role === 'CONTROLLER'`) is in the list but is
 * deliberately NOT in the count (migration 153). It is labelled rather than
 * hidden — otherwise the names would outnumber the count and read like a bug.
 */
function slClientsTooltip(sl: NonNullable<Box["starlink"]>): string {
  const generic =
    "Devices connected to the Starlink router (distinct from Peplink clients)";
  const clients = sl.router_clients;
  if (!clients || clients.length === 0) return generic;
  const lines = clients.map((c) => {
    const name = c.name?.trim() || "(unnamed)";
    return c.role.toUpperCase() === "CONTROLLER"
      ? `${name} — the Starlink router itself (not counted)`
      : `${name} — ${c.conn}`;
  });
  return `${generic}:\n${lines.join("\n")}`;
}

export const MapPopup: React.FC<{
  box: Box;
  operatorView: boolean;
  onRefresh: () => void;
}> = ({ box: b, operatorView, onRefresh }) => {
  const dots = deriveSignalDots(b);
  return (
    <div className="text-sm">
                      <strong>{b.scu_name}</strong>
                      {/* For operators the StackedButton (below) shows the
                          STACKED state + toggle, so skip the inline badge to
                          avoid doubling up; customers still see it here. */}
                      {b.is_stacked && !operatorView && (
                        <span
                          className="ml-2 inline-block text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-secondary-200 text-secondary-800 border border-secondary-400"
                          title={
                            b.stacked_set_by
                              ? `Stacked by ${b.stacked_set_by} (MTTR override)`
                              : "Stacked at source workcenter (jobNo=Stacked)"
                          }
                        >
                          STACKED
                        </span>
                      )}
                      <div className="text-xs text-secondary-500">
                        {b.smartbox_code ? `${b.smartbox_code} · ` : ""}
                        {fmtCustomerWc(b.customer, b.work_center)}
                        {b.location ? ` · ${b.location}` : ""}
                      </div>
                      {/* peplink_address (cached_address) intentionally NOT shown:
                          it's the Peplink account's Houston billing address, the
                          same for every box -- not a real device location. */}
                      {b.is_default_location && (
                        <div
                          className="text-xs text-warning-700 mt-0.5 italic"
                          title="No GPS fix — pin is showing the HQ default location"
                        >
                          📍 Default location (no GPS)
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-2">
                        <StateBadge state={b.state} />
                        {/* v2-rollout posture: PARTIAL / COMPLETE still
                            convey distinct info (which RPI components
                            have been deployed). UNTOUCHED is no longer
                            shown here — the state-based UNTOUCHED chip
                            in StateBadge covers "never onboarded" more
                            accurately. */}
                        {operatorView &&
                          (b.version_status === "PARTIAL" ||
                            b.version_status === "COMPLETE") && (
                            <span
                              className={
                                "px-1.5 py-0.5 rounded text-xs border " +
                                (b.version_status === "PARTIAL"
                                  ? "bg-warning-50 text-warning-700 border-warning-300"
                                  : "bg-success-50 text-success-700 border-success-300")
                              }
                              title={
                                b.version_status === "PARTIAL"
                                  ? "Camera v2 deployed; RFID scanner not yet"
                                  : "v2 stack fully deployed"
                              }
                            >
                              {b.version_status}
                            </span>
                          )}
                      </div>
                      {/* Actual edge software versions (operator-only). Data is
                          gen5_computer.camera_version / rfid_scanner_version via
                          box_state -> the snapshot. */}
                      {operatorView &&
                        (b.version_camera != null || b.version_rfid_scanner != null) && (
                          <div className="mt-1 text-xs text-secondary-600">
                            <span className="text-secondary-400">SW</span>{" "}
                            <span title="Edge camera software version">
                              Cam v{b.version_camera ?? "—"}
                            </span>
                            {" · "}
                            <span title="RFID scanner software version">
                              RFID v{b.version_rfid_scanner ?? "—"}
                            </span>
                          </div>
                        )}
                      {/* NVMe health (operator-only): nvme-cli status + unsafe
                          vs total power cycles. Pis only -- no row, no line. */}
                      {operatorView &&
                        b.nvme &&
                        (() => {
                          const nv = nvmeLine(b.nvme);
                          return (
                            <div
                              className={`text-xs ${nv.tone} cursor-help`}
                              title={nv.title}
                            >
                              <span className="text-secondary-400">NVMe</span>{" "}
                              {nv.text}
                            </div>
                          );
                        })()}
                      {operatorView && (
                        // Connectivity dots on the left, the per-antenna
                        // squares on the right: both answer "what is this box
                        // doing right now", and the strip fills otherwise-dead
                        // space beside the short dot rows.
                        <div className="mt-1 flex items-start justify-between gap-2">
                          <div className="flex flex-col gap-0.5">
                            {/* Per-client icons sit inline with the Pep
                                row — they're a roster of Peplink clients,
                                so visually attaching them to the Pep dot
                                keeps the relationship obvious. The icons
                                alone convey count + breakdown, so no
                                "N clients" prefix is needed. */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <SignalDot label="Pep" {...dots.pep} />
                              {(b.peplink_clients?.length ?? 0) > 0 && (
                                <PeplinkClientIcons
                                  clients={b.peplink_clients ?? []}
                                  max={8}
                                />
                              )}
                            </div>
                            {/* Plug row — single line. Per-outlet `N:on/off`
                                are clickable (micro variant); the dot and
                                the "47d 22h"-style ago text are static. */}
                            <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap flex-wrap">
                              <SignalDot
                                label="Plug"
                                status={dots.plug.status}
                                title={dots.plug.title}
                              />
                              {(b.plug_outlet_count ?? 0) >= 1 && (
                                <PlugOutletResetButton
                                  scu={b.scu_name}
                                  outletNum={1}
                                  outletOn={b.plug_outlet1_on}
                                  outletSinceUtc={b.plug_outlet1_since_utc}
                                  smartboxLabel={b.smartbox_code}
                                  plugIsOnline={b.plug_is_online}
                                  variant="micro"
                                />
                              )}
                              {(b.plug_outlet_count ?? 0) >= 2 && (
                                <PlugOutletResetButton
                                  scu={b.scu_name}
                                  outletNum={2}
                                  outletOn={b.plug_outlet2_on}
                                  outletSinceUtc={b.plug_outlet2_since_utc}
                                  smartboxLabel={b.smartbox_code}
                                  plugIsOnline={b.plug_is_online}
                                  variant="micro"
                                />
                              )}
                              {(() => {
                                // Most-recently-changed outlet drives the "ago".
                                const s1 = b.plug_outlet1_since_utc;
                                const s2 = b.plug_outlet2_since_utc;
                                const recent =
                                  s1 && s2 ? (s1 > s2 ? s1 : s2) : (s1 ?? s2 ?? null);
                                const ago = fmtAgo(recent);
                                return ago ? (
                                  <span className="text-secondary-700">{ago}</span>
                                ) : null;
                              })()}
                            </span>
                            <SignalDot label="UPS" {...dots.ups} />
                            <SignalDot label="TS" {...dots.ts} />
                            {/* Starlink dot — only renders when we have
                                data (status=null suppresses it). */}
                            {dots.sl.status !== null && (
                              <SignalDot label="SL" {...dots.sl} />
                            )}
                          </div>
                          <AntennaStrip scu={b.scu_name} />
                        </div>
                      )}
                      {/* Starlink ROUTER client count — devices hardwired/joined
                          to the Starlink router (distinct from Peplink clients).
                          Only shown where the router was reachable. */}
                      {b.starlink?.router_client_count != null && (
                        <div
                          className="text-[11px] text-secondary-600 cursor-help underline decoration-dotted decoration-secondary-300 underline-offset-2"
                          title={slClientsTooltip(b.starlink)}
                        >
                          SL router: {b.starlink.router_client_count} client
                          {b.starlink.router_client_count === 1 ? "" : "s"}
                        </div>
                      )}
                      {/* Stacked toggle — operators can mark/UN-mark any box
                          straight from the map, no outage ticket needed. */}
                      {operatorView && (
                        <div className="mt-1">
                          <StackedButton
                            scuName={b.scu_name}
                            isStacked={!!b.is_stacked}
                            setBy={b.stacked_set_by}
                            onChanged={() => onRefresh()}
                          />
                        </div>
                      )}
                      {b.lat != null && b.lon != null && (
                        <div className="mt-1 text-xs text-secondary-700">
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${b.lat},${b.lon}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-600 hover:underline"
                            title="Open in Google Maps (new tab)"
                          >
                            {b.lat.toFixed(5)}, {b.lon.toFixed(5)} ↗
                          </a>
                          {b.gps_fix_time_utc && (
                            <span
                              className="ml-2 text-secondary-500"
                              title={`GPS fix at ${b.gps_fix_time_utc} UTC`}
                            >
                              ({fmtAgo(b.gps_fix_time_utc)} ago)
                            </span>
                          )}
                        </div>
                      )}
                      {b.speed_kph != null && b.speed_kph > 5 && (
                        <div className="text-xs text-secondary-700">
                          🚛 {b.speed_kph.toFixed(0)} kph
                          {b.heading_deg != null && (
                            <> @ {b.heading_deg.toFixed(0)}°</>
                          )}
                        </div>
                      )}
                      {b.elapsed_seconds != null && (
                        <div className="mt-1 text-xs">
                          Down for {fmtDuration(b.elapsed_seconds)}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-3">
                        {/* /signal/{scu} is platform-only — same gate as
                            the route in App.tsx and the BoxesPage column.
                            Customers/vendors never see the link. */}
                        {operatorView && (
                          <Link
                            to={`/signal/${encodeURIComponent(b.scu_name)}`}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            Signal details →
                          </Link>
                        )}
                        <Link
                          to={`/inventory/${encodeURIComponent(b.scu_name)}`}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          Inventory →
                        </Link>
                        <ReportMaintenanceButton
                          scu={b.scu_name}
                          label={b.smartbox_code ?? b.scu_name}
                        />
                      </div>
                      {/* Peplink client roster lives inline with the Pep
                          dot above (operatorView SignalDot column). */}
    </div>
  );
};

export default MapPopup;
